/**
 * SQLite Query Executor for Web Demo
 *
 * Executes SQL queries against the sql.js SQLite database and maps
 * results to the shared QueryExecutionResult type.
 */

import type {
  QueryExecutionRequest,
  QueryExecutionResult,
  QueryResultSet,
  QueryColumn,
} from '../../types/queryExecution';
import { getCurrentDatabase, isDatabaseOpen, exportCurrentDatabase, getCurrentDatabaseId } from './sqliteManager';
import { checkpointDatabase } from './demoDatabaseLoader';

/** Default maximum rows to return to prevent UI lockups */
const DEFAULT_MAX_ROWS = 10000;

/** Write operation keywords for checkpoint detection */
const WRITE_KEYWORDS = [
  'INSERT',
  'UPDATE',
  'DELETE',
  'CREATE',
  'DROP',
  'ALTER',
  'REPLACE',
  'TRUNCATE',
];

/**
 * Check if a SQL statement is a write operation (for auto-checkpoint).
 */
function isWriteOperation(sql: string): boolean {
  const upperSql = sql.trim().toUpperCase();
  return WRITE_KEYWORDS.some((keyword) => upperSql.startsWith(keyword));
}

/**
 * Map SQLite value type to a display string.
 */
function getSqliteDataType(value: unknown): string {
  if (value === null) return 'NULL';
  if (typeof value === 'number') {
    return Number.isInteger(value) ? 'INTEGER' : 'REAL';
  }
  if (typeof value === 'string') return 'TEXT';
  if (value instanceof Uint8Array) return 'BLOB';
  return 'TEXT';
}

/**
 * Convert a SQLite value to a serializable format.
 */
function convertValue(value: unknown): unknown {
  if (value === null || value === undefined) return null;
  if (value instanceof Uint8Array) {
    // Convert BLOB to base64 string for display
    return `<BLOB: ${value.length} bytes>`;
  }
  return value;
}

/**
 * Execute a SQL query and return results in the shared format.
 */
export async function executeSqliteQuery(
  request: QueryExecutionRequest
): Promise<QueryExecutionResult> {
  const startTime = performance.now();

  // Check if database is open
  if (!isDatabaseOpen()) {
    return {
      success: false,
      error: { message: 'No database connected. Please connect to a SQLite database first.' },
      resultSets: [],
      messages: [],
    };
  }

  const db = getCurrentDatabase();
  if (!db) {
    return {
      success: false,
      error: { message: 'Database connection lost.' },
      resultSets: [],
      messages: [],
    };
  }

  const maxRows = request.options?.maxRows ?? DEFAULT_MAX_ROWS;

  try {
    // Execute the query using exec which handles multiple statements
    // Note: sql.js exec returns an array of result objects for each statement
    const results = db.exec(request.sql);

    // Map results to QueryResultSet format
    const resultSets: QueryResultSet[] = [];
    let totalRowCount = 0;
    let returnedRowCount = 0;
    let truncated = false;

    for (const result of results) {
      // Get columns from the result
      const columns: QueryColumn[] = result.columns.map((name: string, index: number) => {
        // Infer data type from first row if available
        const firstValue = result.values.length > 0 ? result.values[0][index] : null;
        return {
          name,
          dataType: getSqliteDataType(firstValue),
        };
      });

      // Convert rows to Record<string, unknown> format
      const rowsToReturn = Math.min(result.values.length, maxRows - returnedRowCount);
      const rows: Record<string, unknown>[] = [];

      for (let i = 0; i < rowsToReturn; i++) {
        const row: Record<string, unknown> = {};
        for (let j = 0; j < columns.length; j++) {
          row[columns[j].name] = convertValue(result.values[i][j]);
        }
        rows.push(row);
      }

      totalRowCount += result.values.length;
      returnedRowCount += rows.length;

      if (result.values.length > rowsToReturn) {
        truncated = true;
      }

      resultSets.push({
        columns,
        rows,
        rowCount: rows.length,
      });

      // Stop adding result sets if we've hit the max rows
      if (returnedRowCount >= maxRows) {
        truncated = true;
        break;
      }
    }

    const executionTimeMs = Math.round(performance.now() - startTime);

    // Auto-checkpoint after write operations
    const isWriteQuery = isWriteOperation(request.sql);
    if (isWriteQuery) {
      const databaseId = getCurrentDatabaseId();
      const exported = exportCurrentDatabase();
      if (databaseId && exported) {
        // Fire and forget - don't block the query response
        checkpointDatabase(databaseId, exported).then((result) => {
          if (!result.success && result.reason) {
            console.log('[SQLite] Auto-checkpoint skipped:', result.reason);
          }
        });
      }
    }

    return {
      success: true,
      resultSets,
      messages: [],
      stats: {
        executionTimeMs,
        totalRowCount,
        returnedRowCount,
        truncated,
      },
    };
  } catch (error) {
    const executionTimeMs = Math.round(performance.now() - startTime);
    const errorMessage = error instanceof Error ? error.message : 'Query execution failed';

    return {
      success: false,
      error: { message: errorMessage },
      resultSets: [],
      messages: [{ type: 'error', text: errorMessage }],
      stats: {
        executionTimeMs,
      },
    };
  }
}

/**
 * Cancel a running query.
 * Note: sql.js runs synchronously, so this is effectively a no-op.
 * In a future P1 slice, we could use a Web Worker to enable true cancellation.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function cancelSqliteQuery(_executionId: string): { success: boolean; error?: string } {
  // sql.js runs synchronously on the main thread, so queries cannot be cancelled.
  // This is a documented limitation of the P0 implementation.
  return {
    success: false,
    error: 'SQLite queries cannot be cancelled in browser mode (synchronous execution).',
  };
}
